(function () {
  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      cents / 100
    );
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  class InlineQuickAdd extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('.inline-qa-data');
      if (!dataEl) return;

      try {
        this.product = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('InlineQuickAdd: failed to parse product data', e);
        return;
      }

      const panelId = this.dataset.panel;
      this.panel = document.getElementById(panelId);
      if (!this.panel) return;

      this.trigger = this.querySelector('.inline-qa__trigger');
      this.closeBtn = this.panel.querySelector('.inline-qa__close');
      this.optionsEl = this.panel.querySelector('.inline-qa__options');
      this.purchaseOptionsEl = this.panel.querySelector('.inline-qa__purchase-options');
      this.addBtn = this.panel.querySelector('.inline-qa__add');
      this.addTextEl = this.panel.querySelector('.inline-qa__add-text');
      this.priceEl = this.panel.querySelector('.inline-qa__price');
      this.errorEl = this.panel.querySelector('.inline-qa__error');

      this.selectedOptions = this.product.variants[0].options.slice();
      this.selectedSellingPlanId = null;

      this.renderOptions();
      this.renderPurchaseOptions();
      this.updatePrice();

      this.trigger.addEventListener('click', () => this.openPanel());
      this.closeBtn.addEventListener('click', () => this.closePanel());
      this.addBtn.addEventListener('click', () => this.addToCart());

      document.addEventListener('click', (e) => {
        if (this.panel.hasAttribute('hidden')) return;
        const card = this.closest('.product-card-wrapper');
        if (card && !card.contains(e.target)) this.closePanel();
      });
    }

    get selectedVariant() {
      return (
        this.product.variants.find((v) =>
          v.options.every((opt, i) => opt === this.selectedOptions[i])
        ) || this.product.variants[0]
      );
    }

    openPanel() {
      // Close any other open panels first
      document.querySelectorAll('.inline-qa__panel:not([hidden])').forEach((el) => {
        if (el !== this.panel) el.setAttribute('hidden', '');
      });
      this.panel.removeAttribute('hidden');
      this.closeBtn.focus();
    }

    closePanel() {
      // Blur any focused child before hiding — prevents the aria-hidden-on-focused-ancestor warning
      // which cascades into cart-drawer's trapFocus crashing on null
      const focused = this.panel.querySelector(':focus');
      if (focused) focused.blur();
      this.panel.setAttribute('hidden', '');
      if (this.errorEl) this.errorEl.setAttribute('hidden', '');
    }

    renderOptions() {
      if (this.product.variants.length <= 1) return;

      // Keep original index so data-option-index always maps to product.variants[n].options[i]
      const visibleOptions = this.product.options
        .map((option, i) => ({ option, i }))
        .filter(({ option }) => !(option.values.length === 1 && option.values[0] === 'Default Title'));

      if (!visibleOptions.length) return;

      this.optionsEl.innerHTML = visibleOptions
        .map(
          ({ option, i }) => `
          <div class="inline-qa__option-group">
            <p class="inline-qa__option-name">${option.name}:</p>
            <div class="inline-qa__option-pills">
              ${option.values
                .map(
                  (value) =>
                    `<button type="button"
                      class="inline-qa__pill${this.selectedOptions[i] === value ? ' active' : ''}"
                      data-option-index="${i}"
                      data-option-value="${escHtml(value)}">
                      ${escHtml(value)}
                    </button>`
                )
                .join('')}
            </div>
          </div>`
        )
        .join('');

      this.optionsEl.querySelectorAll('.inline-qa__pill').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.optionIndex, 10);
          this.selectedOptions[idx] = btn.dataset.optionValue;
          this.optionsEl
            .querySelectorAll(`[data-option-index="${idx}"]`)
            .forEach((b) =>
              b.classList.toggle('active', b.dataset.optionValue === btn.dataset.optionValue)
            );
          this.updatePrice();
        });
      });
    }

    renderPurchaseOptions() {
      const groups = this.product.selling_plan_groups;
      if (!groups || groups.length === 0) return;

      const firstPlan = groups[0].selling_plans[0];
      if (!firstPlan) return;

      let discountLabel = 'Subscribe &amp; Save';
      if (firstPlan.price_adjustments && firstPlan.price_adjustments.length > 0) {
        const adj = firstPlan.price_adjustments[0];
        if (adj.value_type === 'percentage') {
          discountLabel = `Subscribe &amp; Save ${adj.value}%`;
        }
      }

      this.purchaseOptionsEl.innerHTML = `
        <div class="inline-qa__purchase-row active" data-plan-id="">
          <span class="inline-qa__radio-dot"></span>
          <span>One-Time Purchase</span>
        </div>
        <div class="inline-qa__purchase-row" data-plan-id="${firstPlan.id}">
          <span class="inline-qa__radio-dot"></span>
          <span>${discountLabel}</span>
        </div>`;

      this.purchaseOptionsEl.querySelectorAll('.inline-qa__purchase-row').forEach((row) => {
        row.addEventListener('click', () => {
          this.purchaseOptionsEl
            .querySelectorAll('.inline-qa__purchase-row')
            .forEach((r) => r.classList.remove('active'));
          row.classList.add('active');
          this.selectedSellingPlanId = row.dataset.planId || null;
          this.updatePrice();
        });
      });
    }

    updatePrice() {
      const variant = this.selectedVariant;
      if (!variant) return;

      let price = variant.price;

      if (this.selectedSellingPlanId) {
        outer: for (const group of this.product.selling_plan_groups) {
          for (const plan of group.selling_plans) {
            if (String(plan.id) === String(this.selectedSellingPlanId)) {
              const adjs = plan.price_adjustments;
              if (adjs && adjs.length > 0) {
                const adj = adjs[0];
                if (adj.value_type === 'percentage') {
                  price = Math.round(price * (1 - adj.value / 100));
                } else if (adj.value_type === 'fixed_amount') {
                  price = price - adj.value;
                } else if (adj.value_type === 'price') {
                  price = adj.value;
                }
              }
              break outer;
            }
          }
        }
      }

      if (variant.available) {
        this.addBtn.disabled = false;
        if (this.addTextEl) this.addTextEl.textContent = 'Add 1 for ';
        if (this.priceEl) this.priceEl.textContent = formatMoney(price);
      } else {
        this.addBtn.disabled = true;
        if (this.addTextEl) this.addTextEl.textContent = 'Sold Out';
        if (this.priceEl) this.priceEl.textContent = '';
      }
    }

    async addToCart() {
      const variant = this.selectedVariant;
      if (!variant || !variant.available) return;

      const cart =
        document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      const spinner = this.addBtn.querySelector('.loading__spinner');
      this.addBtn.setAttribute('aria-disabled', 'true');
      this.addBtn.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
      if (this.errorEl) this.errorEl.setAttribute('hidden', '');

      // Match product-form.js exactly: FormData + X-Requested-With, no Content-Type
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const sectionIds = cart ? cart.getSectionsToRender().map((s) => s.id) : [];

      const formData = new FormData();
      formData.append('id', variant.id);
      formData.append('quantity', 1);
      if (this.selectedSellingPlanId) {
        formData.append('selling_plan', this.selectedSellingPlanId);
      }
      if (cart) {
        formData.append('sections', sectionIds);
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      try {
        const response = await fetch(window.routes.cart_add_url, config);
        const data = await response.json();

        if (data.status) {
          if (this.errorEl) {
            this.errorEl.textContent = data.description || data.message || 'Error adding to cart';
            this.errorEl.removeAttribute('hidden');
          }
          return;
        }

        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'inline-quick-add',
            productVariantId: variant.id,
            cartData: data,
          });
        }

        if (cart) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(data);
        }
        this.closePanel();
      } catch (e) {
        console.error('[IQA] fetch failed:', e);
      } finally {
        this.addBtn.removeAttribute('aria-disabled');
        this.addBtn.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      }
    }
  }

  if (!customElements.get('inline-quick-add')) {
    customElements.define('inline-quick-add', InlineQuickAdd);
  }
})();
